import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Car, Plus, MessageCircle, Wrench, Gauge, Fuel, X, DollarSign, Clock, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import * as Drawer from "vaul";
import { useCar } from '@/contexts/CarContext';
import HomeIcon from '@/components/icons/HomeIcon';
import HistoryIcon from '@/components/icons/HistoryIcon';
import UserIcon from '@/components/icons/UserIcon';
import { useI18n } from '@/i18n/LanguageContext';
import { useKeyboardVisibility } from '@/hooks/useKeyboardVisibility';
import AIAssistantComingSoonModal from '@/components/AIAssistantComingSoonModal';

const NavBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedCarId } = useCar();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [aiAssistantModalOpen, setAIAssistantModalOpen] = useState(false);
  const isKeyboardVisible = useKeyboardVisibility();
  
  const navItems = [
    { key: 'home', iconBase: 'Home', label: t('nav.home'), path: '/home' },
    { key: 'cars', iconBase: 'My Cars', label: t('nav.myCars'), path: '/cars' },
    { key: 'center', iconBase: null as any, label: null as any, path: null as any, center: true },
    { key: 'records', iconBase: 'Records', label: t('nav.records'), path: '/records' },
    { key: 'assistant', iconBase: 'AI', label: t('nav.assistant'), path: null as any },
  ];

  const handleAddNewClick = () => {
    setOpen(true);
  };

  // Brand styles per mock (Issue, Reminder, Service, Cost, Odometer, Refuel)
  const brandStyles = [
    { bg: 'bg-[#eff2fc]', icon: 'text-[#7c9ffa]' },
    { bg: 'bg-[#e3fafb]', icon: 'text-[#13e2e9]' },
    { bg: 'bg-[#ebfce7]', icon: 'text-[#2fab71]' },
    { bg: 'bg-[#fff4e6]', icon: 'text-[#ff8a00]' },
    { bg: 'bg-[#e7fe52]/30', icon: 'text-[#a2b800]' },
    { bg: 'bg-[#fbe7fa]', icon: 'text-[#a43fbf]' },
  ];

  const actions = [
    { icon: <AlertCircle size={20} />, label: t('nav.issue'), onClick: () => navigate('/log-symptom') },
    { icon: <Clock size={20} />, label: t('nav.task'), onClick: () => navigate(`/tasks/add${selectedCarId ? `?carId=${selectedCarId}` : ''}`) },
    { icon: <Wrench size={20} />, label: t('nav.serviceEntry'), onClick: () => navigate(`/services/add${selectedCarId ? `?carId=${selectedCarId}` : ''}`) },
    { icon: <DollarSign size={20} />, label: t('nav.expense'), onClick: () => navigate(`/expenses/add${selectedCarId ? `?carId=${selectedCarId}` : ''}`) },
    { icon: <Gauge size={20} />, label: t('nav.odometer'), onClick: () => navigate('/odometer/add') },
    { icon: <Fuel size={20} />, label: t('nav.refueling'), onClick: () => navigate(`/expenses/add${selectedCarId ? `?carId=${selectedCarId}&category=Refueling` : '?category=Refueling'}`) },
  ];

  return (
    <>
      {!open && !isKeyboardVisible && (
      <nav className="fixed bottom-0 left-0 right-0 bg-[#F5F5F5] border-t border-gray-200 py-2 px-4 z-50">
        <div className="grid grid-cols-5 gap-1">
          {navItems.map((item) => {
            const isActive = item.path ? (location.pathname === item.path || 
              (item.path !== '/home' && location.pathname.startsWith(item.path))) : false;

            if (item.center) {
              return (
                <button 
                  key="add-new"
                  onClick={handleAddNewClick}
                  className="flex flex-col items-center -mt-6"
                >
                  <div className="bg-lime rounded-full p-4 mb-1 shadow-md">
                    <Plus size={24} className="text-black" />
                  </div>
                </button>
              );
            }

            const iconSrc = `/icons/NavBar Icons/${item.iconBase}_${isActive ? 'selected' : 'de'}.png`;
            if (item.key === 'assistant') {
              const aiIcon = `/icons/NavBar Icons/AI_de.png`;
              return (
                <button
                  key={item.key}
                  onClick={() => setAIAssistantModalOpen(true)}
                  className="flex flex-col items-center"
                >
                  <img src={aiIcon} alt="AI" className="w-6 h-6 mb-1" />
                  <span className="text-xs" style={{ color: '#5d5d5d', fontWeight: 400 }}>{t('nav.assistant')}</span>
                </button>
              );
            }
            return (
              <Link 
                key={item.label as string} 
                to={item.path as string}
                className="flex flex-col items-center"
              >
                <img
                  src={iconSrc}
                  alt=""
                  className="w-6 h-6 mb-1"
                  onError={(e) => {
                    try { (e.currentTarget as HTMLImageElement).style.display = 'none'; } catch {}
                  }}
                />
                <span
                  className="text-xs"
                  style={{ color: isActive ? '#090909' : '#5d5d5d', fontWeight: 400 }}
                >
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>
      )}

      <Drawer.Root open={open} onOpenChange={setOpen} dismissible>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/40 z-[60]" />
          <Drawer.Content className="fixed left-0 right-0 bottom-0 rounded-t-2xl bg-white shadow-2xl z-[70]">
            <div className="relative pt-3 pb-2">
              <button
                aria-label="Close"
                onClick={() => setOpen(false)}
                className="absolute -top-20 left-1/2 -translate-x-1/2 bg-white border border-gray-200 rounded-full w-10 h-10 flex items-center justify-center shadow-lg"
              >
                <X className="text-gray-700" size={18} />
              </button>
              <div className="mx-auto h-1 w-12 rounded-full bg-gray-300" />
            </div>
            <div className="grid grid-cols-3 w-full gap-4 px-4 pb-2">
              {actions.map((action, i) => (
                <button
                  key={i}
                  onClick={() => { action.onClick(); setOpen(false); }}
                  className="flex flex-col items-center justify-center gap-2 py-2"
                >
                  <div className={`w-14 h-14 flex items-center justify-center rounded-2xl shadow-md ${brandStyles[i]?.bg || 'bg-gray-100'}`}>
                    <div className={`${brandStyles[i]?.icon || 'text-gray-700'}`}>{action.icon}</div>
                  </div>
                  <span className="text-sm font-normal text-gray-800" style={{ fontFamily: 'Lato', fontWeight: 400 }}>{action.label}</span>
                </button>
              ))}
            </div>
            <div className="pb-[calc(env(safe-area-inset-bottom)+16px)]" />
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>

      <AIAssistantComingSoonModal
        open={aiAssistantModalOpen}
        onOpenChange={setAIAssistantModalOpen}
      />
    </>
  );
};

export default NavBar;
